import { useState } from 'react';
import { Link } from 'react-router-dom';

type Step = 'goal' | 'describe' | 'brand' | 'building' | 'dashboard';

export default function FlowView() {
  const [step, setStep] = useState<Step>('goal');
  const [goal, setGoal] = useState('');
  const [description, setDescription] = useState('');
  const [brandChoice, setBrandChoice] = useState('');
  const [buildProgress, setBuildProgress] = useState(0);

  const steps: { id: Step; label: string }[] = [
    { id: 'goal', label: 'Start' },
    { id: 'describe', label: 'Describe' },
    { id: 'brand', label: 'Brand' },
    { id: 'building', label: 'Launch' },
  ];

  const currentIdx = steps.findIndex((s) => s.id === step);

  const startBuild = () => {
    setStep('building');
    setBuildProgress(0);
    const interval = setInterval(() => {
      setBuildProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => setStep('dashboard'), 600);
          return 100;
        }
        return prev + 2;
      });
    }, 60);
  };

  const renderGoal = () => (
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">What brings you here today?</h2>
      <p className="text-zinc-400 mb-10">Every great site starts with a clear goal.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            id: 'new',
            icon: 'add_circle',
            title: 'Build a New Site',
            desc: 'Start fresh with AI assistance',
          },
          {
            id: 'migrate',
            icon: 'swap_horiz',
            title: 'Move My Site',
            desc: 'Migrate from another host',
          },
          {
            id: 'facelift',
            icon: 'auto_fix_high',
            title: 'Refresh My Site',
            desc: 'Give an existing site a facelift',
          },
        ].map((option) => (
          <button
            key={option.id}
            onClick={() => {
              setGoal(option.id);
              setStep('describe');
            }}
            className={`group p-6 bg-zinc-900/60 border rounded-2xl transition-all text-center hover:-translate-y-1 ${
              goal === option.id
                ? 'border-orange-500 bg-orange-500/10'
                : 'border-zinc-800 hover:border-orange-500/40'
            }`}
          >
            <span className="material-icons text-4xl text-zinc-500 group-hover:text-orange-400 transition mb-4 block">
              {option.icon}
            </span>
            <p className="text-base font-semibold text-white mb-1">{option.title}</p>
            <p className="text-xs text-zinc-500">{option.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const renderDescribe = () => (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">
        {goal === 'new' && 'Describe your vision'}
        {goal === 'migrate' && 'Where is your current site?'}
        {goal === 'facelift' && "What needs a refresh?"}
      </h2>
      <p className="text-zinc-400 mb-8 text-center">
        {goal === 'new' && "Tell me what your site is about. The more detail, the better I can help."}
        {goal === 'migrate' && "Paste your site URL and I'll analyze it, then recreate it on WiserWebCloud."}
        {goal === 'facelift' && "Describe what you'd like to change. I'll handle the rest."}
      </p>

      <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6">
        {goal === 'migrate' ? (
          <div className="flex items-center bg-zinc-800/60 border border-zinc-700/50 rounded-xl px-3 mb-4">
            <span className="material-icons text-zinc-500">link</span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="https://my-current-site.com"
              className="flex-1 bg-transparent px-3 py-3 text-sm text-white placeholder:text-zinc-600 outline-none"
            />
          </div>
        ) : (
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              goal === 'new'
                ? "e.g., A bakery website with a menu, online ordering, opening hours, and a gallery of our products..."
                : "e.g., Update the design to feel more modern, add a contact form, improve the mobile layout..."
            }
            className="w-full h-32 bg-zinc-800/60 border border-zinc-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none resize-none"
          />
        )}

        {/* Quick suggestion chips */}
        {goal === 'new' && (
          <div className="flex flex-wrap gap-2 mt-3">
            {['Restaurant', 'Portfolio', 'Online Store', 'Blog', 'Business Landing'].map((s) => (
              <button
                key={s}
                onClick={() => setDescription((prev) => (prev ? `${prev}, ${s.toLowerCase()}` : `A ${s.toLowerCase()} website`))}
                className="px-3 py-1 bg-zinc-800/60 border border-zinc-700/50 rounded-full text-xs text-zinc-400 hover:text-orange-300 hover:border-orange-500/40 transition"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={() => setStep('brand')}
            disabled={!description.trim()}
            className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-semibold rounded-xl hover:shadow-glow transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue
            <span className="material-icons text-sm ml-1 align-middle">arrow_forward</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderBrand = () => (
    <div className="max-w-xl mx-auto text-center">
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Branding</h2>
      <p className="text-zinc-400 mb-8">Do you have branding assets, or should I create them?</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          {
            id: 'have',
            icon: 'cloud_upload',
            title: 'I have branding',
            desc: 'Upload logo & brand colors',
          },
          {
            id: 'create',
            icon: 'auto_awesome',
            title: 'Create for me',
            desc: 'AI generates branding',
          },
          {
            id: 'skip',
            icon: 'skip_next',
            title: 'Skip for now',
            desc: 'Use defaults, change later',
          },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setBrandChoice(opt.id)}
            className={`group p-5 bg-zinc-900/60 border rounded-xl transition-all ${
              brandChoice === opt.id
                ? 'border-orange-500 bg-orange-500/10'
                : 'border-zinc-800 hover:border-orange-500/40'
            }`}
          >
            <span className="material-icons text-3xl text-zinc-500 group-hover:text-orange-400 transition mb-3 block">
              {opt.icon}
            </span>
            <p className="text-sm font-semibold text-white mb-1">{opt.title}</p>
            <p className="text-xs text-zinc-500">{opt.desc}</p>
          </button>
        ))}
      </div>

      {brandChoice === 'have' && (
        <div className="bg-zinc-900/60 border border-dashed border-zinc-700 rounded-xl p-8 mb-6 animate-fade-in">
          <span className="material-icons text-4xl text-zinc-600 mb-2 block">cloud_upload</span>
          <p className="text-sm text-zinc-400">Drag your logo here or click to upload</p>
          <p className="text-xs text-zinc-600 mt-1">PNG, SVG, or JPG up to 5MB</p>
        </div>
      )}

      {brandChoice === 'create' && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 mb-6 animate-fade-in">
          <p className="text-sm text-zinc-300 mb-3">Pick a style direction:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Modern & Clean', 'Warm & Friendly', 'Bold & Vibrant', 'Elegant & Classic'].map(
              (style) => (
                <button
                  key={style}
                  className="px-3 py-1.5 bg-zinc-800/60 border border-zinc-700/50 rounded-lg text-xs text-zinc-400 hover:text-orange-300 hover:border-orange-500/40 transition"
                >
                  {style}
                </button>
              ),
            )}
          </div>
        </div>
      )}

      {brandChoice && (
        <button
          onClick={startBuild}
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-glow transition-all animate-fade-in"
        >
          <span className="material-icons text-sm mr-1 align-middle">rocket_launch</span>
          Build My Site
        </button>
      )}
    </div>
  );

  const renderBuilding = () => (
    <div className="max-w-md mx-auto text-center">
      <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-2xl flex items-center justify-center">
        <span className="material-icons text-3xl text-orange-400 deploy-spin">autorenew</span>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Building your site...</h2>
      <p className="text-zinc-400 mb-8 text-sm">This usually takes about 10 seconds.</p>

      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-100"
          style={{ width: `${buildProgress}%` }}
        />
      </div>

      <div className="space-y-2 text-left max-w-xs mx-auto">
        {[
          { label: 'Analyzing your requirements', done: buildProgress > 20 },
          { label: 'Generating layout & design', done: buildProgress > 45 },
          { label: 'Creating content & pages', done: buildProgress > 70 },
          { label: 'Deploying to WiserWebCloud', done: buildProgress > 90 },
        ].map((task) => (
          <div key={task.label} className="flex items-center gap-2 text-sm">
            <span className={`material-icons text-sm ${task.done ? 'text-emerald-400' : 'text-zinc-600'}`}>
              {task.done ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            <span className={task.done ? 'text-zinc-300' : 'text-zinc-600'}>{task.label}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="max-w-3xl mx-auto">
      {/* Success banner */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6 flex items-center gap-3 animate-fade-in-up">
        <span className="material-icons text-emerald-400 text-xl">celebration</span>
        <div>
          <p className="text-sm font-medium text-emerald-300">Your site is live!</p>
          <p className="text-xs text-zinc-400">
            Available at{' '}
            <span className="text-orange-400 font-mono">fresh-bakery.wiserwebhost.com</span>
          </p>
        </div>
      </div>

      {/* Site card with preview */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden mb-6">
        {/* Mini preview */}
        <div className="h-48 bg-gradient-to-br from-amber-800/20 to-orange-700/20 flex items-center justify-center relative">
          <div className="text-center">
            <p className="text-xl font-bold text-orange-200/60">Fresh Bakery</p>
            <p className="text-xs text-zinc-500 mt-1">Artisan breads & pastries</p>
          </div>
          <div className="absolute top-3 right-3 flex gap-2">
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded-full font-medium flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
              Live
            </span>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-zinc-800">
          {[
            { icon: 'edit_note', label: 'Edit Content' },
            { icon: 'palette', label: 'Design' },
            { icon: 'smart_toy', label: 'AI Help' },
            { icon: 'backup', label: 'Backups' },
          ].map((action) => (
            <button
              key={action.label}
              className="flex items-center justify-center gap-2 py-3 text-sm text-zinc-400 hover:text-orange-400 hover:bg-orange-500/5 border-r border-zinc-800 last:border-r-0 transition"
            >
              <span className="material-icons text-lg">{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* AI Suggestions */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="material-icons text-orange-400">auto_awesome</span>
          <h3 className="text-sm font-medium text-white">AI Suggestions</h3>
        </div>
        {['Add opening hours to your homepage', 'Your images could load 40% faster', 'A testimonials section would boost trust'].map(
          (s) => (
            <div
              key={s}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-zinc-800/30 border border-zinc-700/30 mb-2 last:mb-0"
            >
              <span className="text-sm text-zinc-300">{s}</span>
              <button className="shrink-0 text-xs text-orange-400 hover:text-orange-300 transition">
                Apply
              </button>
            </div>
          ),
        )}
      </div>

      {/* Chat CTA */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
        <div className="flex items-center gap-3">
          <span className="material-icons text-2xl text-orange-400">chat</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-white">Continue working with AI</p>
            <p className="text-xs text-zinc-500">
              Ask me to change anything — text, design, add pages, SEO...
            </p>
          </div>
          <button className="px-4 py-2 bg-orange-500/15 border border-orange-500/30 rounded-lg text-sm text-orange-400 hover:bg-orange-500/25 transition">
            Open Chat
          </button>
        </div>
      </div>

      {/* Mobile device hint */}
      <div className="mt-6 p-3 bg-zinc-800/30 border border-zinc-700/30 rounded-lg flex items-start gap-2 md:hidden">
        <span className="material-icons text-sky-400 text-lg shrink-0">phone_iphone</span>
        <p className="text-xs text-zinc-500">
          Content editing works great on mobile! For layout and design changes,
          we recommend switching to a desktop or laptop.
        </p>
      </div>

      {/* Switch to advanced */}
      <div className="mt-6 text-center">
        <button className="text-xs text-zinc-600 hover:text-zinc-400 transition">
          Switch to Advanced View
          <span className="material-icons text-xs ml-1 align-middle">arrow_forward</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen bg-base flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="h-12 flex items-center justify-between px-4 md:px-6 border-b border-zinc-800 bg-zinc-950/80 shrink-0">
        <Link to="/mockups" className="text-orange-500 font-bold text-sm hover:text-orange-400 transition">
          WiserWeb
        </Link>
        {step !== 'dashboard' && step !== 'building' && (
          <div className="flex items-center gap-1">
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition ${
                    i <= currentIdx
                      ? 'bg-orange-500 text-white'
                      : 'bg-zinc-800 text-zinc-600'
                  }`}
                >
                  {i < currentIdx ? (
                    <span className="material-icons text-xs">check</span>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`w-8 h-0.5 mx-1 transition ${
                      i < currentIdx ? 'bg-orange-500' : 'bg-zinc-800'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {step === 'dashboard' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 hidden sm:inline">Standard View</span>
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-xs font-bold text-white">
              S
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 md:px-8 py-8 md:py-12">
          {step === 'goal' && renderGoal()}
          {step === 'describe' && renderDescribe()}
          {step === 'brand' && renderBrand()}
          {step === 'building' && renderBuilding()}
          {step === 'dashboard' && renderDashboard()}
        </div>
      </div>

      {/* Back button for wizard steps */}
      {step !== 'goal' && step !== 'building' && step !== 'dashboard' && (
        <div className="p-4 border-t border-zinc-800 shrink-0">
          <button
            onClick={() => {
              if (step === 'describe') setStep('goal');
              if (step === 'brand') setStep('describe');
            }}
            className="text-sm text-zinc-500 hover:text-zinc-300 transition flex items-center gap-1"
          >
            <span className="material-icons text-sm">arrow_back</span>
            Back
          </button>
        </div>
      )}
    </div>
  );
}
